import React, { useState } from 'react';
import { Clock, HelpCircle, X, Megaphone, Crown, LogOut } from 'lucide-react';
import { useBreakoutRooms } from '../../../context/BreakoutRoomContext';

function formatTimer(seconds) {
  if (seconds == null) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

function getTimerColor(seconds) {
  if (seconds == null) return 'text-gray-400';
  if (seconds > 300) return 'text-[#00c853]';
  if (seconds > 60) return 'text-[#ffa500]';
  return 'text-red-400 animate-pulse';
}

export default function BreakoutRoomBanner() {
  const [helpRequested, setHelpRequested] = useState(false);
  const [dismissedMessages, setDismissedMessages] = useState(new Set());

  const {
    currentBreakoutRoom,
    timerRemaining,
    broadcastMessages,
    breakoutModeratorName,
    isBreakoutModerator,
    requestHelp,
    cancelHelp,
    leaveBreakoutAndReturn,
  } = useBreakoutRooms();

  if (!currentBreakoutRoom) return null;

  const handleHelpToggle = () => {
    if (helpRequested) {
      cancelHelp();
      setHelpRequested(false);
    } else {
      requestHelp();
      setHelpRequested(true);
    }
  };

  const visibleMessages = broadcastMessages.filter(
    (m) => !dismissedMessages.has(m.sentAt)
  );

  return (
    <>
      {/* Main Banner */}
      <div className="bg-[#6a1fbf]/15 border-b border-[#6a1fbf]/30 px-3 py-2 space-y-2">
        {/* Row 1: Room name + timer */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-2 h-2 bg-[#6a1fbf] rounded-full flex-shrink-0" />
            <span className="text-white text-xs font-medium truncate" title={currentBreakoutRoom.roomName}>
              {currentBreakoutRoom.roomName}
            </span>
            {(breakoutModeratorName || isBreakoutModerator) && (
              <span className="flex items-center gap-1 text-[10px] text-yellow-400/80 flex-shrink-0">
                <Crown className="w-2.5 h-2.5" />
                {isBreakoutModerator ? 'You are Room Host' : breakoutModeratorName}
              </span>
            )}
          </div>
          {timerRemaining != null && (
            <div className={`flex items-center gap-1 text-xs font-mono flex-shrink-0 ${getTimerColor(timerRemaining)}`}>
              <Clock className="w-3 h-3" />
              {formatTimer(timerRemaining)}
            </div>
          )}
        </div>

        {/* Row 2: Action buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleHelpToggle}
            className={`flex-1 py-1 rounded-lg text-[11px] font-medium flex items-center justify-center gap-1 transition-colors ${
              helpRequested
                ? 'bg-red-500/20 border border-red-500/30 text-red-400'
                : 'bg-white/10 border border-white/10 text-white hover:bg-white/15'
            }`}
          >
            <HelpCircle className="w-3 h-3" />
            {helpRequested ? 'Requested' : 'Help'}
          </button>
          <button
            onClick={() => leaveBreakoutAndReturn()}
            className="flex-1 py-1 rounded-lg text-[11px] font-medium flex items-center justify-center gap-1 bg-white/10 border border-white/10 text-white hover:bg-red-500/20 hover:border-red-500/30 hover:text-red-400 transition-colors"
          >
            <LogOut className="w-3 h-3" />
            Leave
          </button>
        </div>
      </div>

      {/* Broadcast Messages */}
      {visibleMessages.length > 0 && (
        <div className="px-4 pt-2 space-y-2">
          {visibleMessages.map((msg, idx) => (
            <div
              key={msg.sentAt || idx}
              className={`rounded-lg p-3 flex items-start justify-between ${
                msg.isWarning
                  ? 'bg-[#ffa500]/20 border border-[#ffa500]/30'
                  : 'bg-[#6a1fbf]/20 border border-[#6a1fbf]/30'
              }`}
            >
              <div className="flex items-start gap-2">
                <Megaphone className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                  msg.isWarning ? 'text-[#ffa500]' : 'text-[#6a1fbf]'
                }`} />
                <div>
                  <p className="text-white text-sm">{msg.message}</p>
                  <p className="text-gray-400 text-xs mt-0.5">From {msg.from}</p>
                </div>
              </div>
              <button
                onClick={() => setDismissedMessages(prev => new Set([...prev, msg.sentAt]))}
                className="p-1 hover:bg-white/10 rounded transition-colors flex-shrink-0"
              >
                <X className="w-3 h-3 text-gray-400" />
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
